import { datadogLogs } from '@datadog/browser-logs'
import type { LogsInitConfiguration } from '@datadog/browser-logs'
import { createBeforeSend, isBotUserAgent } from '@/services/datadogLogPolicy'
import { hostOf } from '@/utilities/url'
import type { LogLevel } from '@/services/datadogLogPolicy'

/**
 * Datadog Logs設定用のインターフェース
 * 環境変数から読み込んだ設定値を保持する
 */
interface DatadogConfig {
  clientToken: string                     // DatadogのクライアントトークンID（認証用）
  site: LogsInitConfiguration['site']     // Datadogのリージョン（例: datadoghq.com, datadoghq.eu）
  service: string                         // アプリケーション名（例: academy。サーバ側 APM / ログと同じ値）
  env: string                             // 環境名（例: development, staging, production）
  version: string                         // アプリケーションのバージョン
  sessionSampleRate: number               // ログ収集のサンプルレート（0-100%）
  forwardErrorsToLogs: boolean            // JavaScriptエラーを自動的にログに転送するかどうか
  forwardConsoleLogs: LogsInitConfiguration['forwardConsoleLogs']  // console.logの内容をDatadogに転送する設定
}

/**
 * Datadog Logsサービスクラス
 * エラートラッキング、イベントログ、ユーザーコンテキスト管理などの機能を提供
 */
class DatadogService {
  private initialized = false  // 初期化済みフラグ

  /**
   * Datadog Logsを初期化する
   * アプリケーション起動時に一度だけ実行される
   */
  initialize(): void {
    // 二重初期化を防ぐ
    if (this.initialized) {
      console.warn('Datadog Logs is already initialized')
      return
    }

    // クローラ・ヘッドレスブラウザからは送らない（#8882）
    if (isBotUserAgent(navigator.userAgent)) {
      return
    }

    // 環境変数から設定を取得
    const config = this.getConfig()
    if (!config) {
      console.warn('Datadog configuration is missing. Skipping initialization.')
      return
    }

    // Datadog Logsを初期化（設定値を渡す）
    datadogLogs.init({
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      forwardErrorsToLogs: config.forwardErrorsToLogs,
      forwardConsoleLogs: config.forwardConsoleLogs,
      sessionSampleRate: config.sessionSampleRate,
      // 送る/送らない・レベルの判定は datadogLogPolicy に集約している
      beforeSend: createBeforeSend(this.firstPartyHosts()),
    })

    // ログイン中なら Vue mount 前のエラー（chunk 読み込み失敗等）にも user_id が付くよう、layout の data-user-id から先に設定する。
    // mount 後は stores/user.ts の watch が setUser / clearUser で引き継ぐ
    const userId = document.body.dataset.userId
    if (userId) {
      datadogLogs.setGlobalContextProperty('user_id', userId)
    }

    // 初期化完了フラグを設定
    this.initialized = true
    console.log('Datadog Logs initialized successfully')
  }

  /**
   * 環境変数からDatadog設定を取得する
   * @returns DatadogConfig | null - 設定オブジェクト、または必須設定が不足している場合はnull
   */
  private getConfig(): DatadogConfig | null {
    // クライアントトークンは必須
    const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN

    if (!clientToken) {
      return null
    }

    // デフォルト値を使用して設定オブジェクトを構築
    const site = import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com'

    return {
      clientToken,
      site: site as LogsInitConfiguration['site'],
      service: import.meta.env.VITE_DATADOG_SERVICE || 'academy',
      env: import.meta.env.VITE_DATADOG_ENV || 'development',
      version: import.meta.env.VITE_DATADOG_VERSION || '1.0.0',
      sessionSampleRate: Number(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE) || 100,
      forwardErrorsToLogs: import.meta.env.VITE_DATADOG_FORWARD_ERRORS_TO_LOGS !== 'false',
      forwardConsoleLogs: import.meta.env.VITE_DATADOG_FORWARD_CONSOLE_LOGS === 'true' ? 'all' : undefined,
    }
  }

  /**
   * 自ドメインとみなすホスト一覧（network エラーを送る対象・source エラーの帰属判定に使う）
   * ページ自身、API、romanee API、バンドルの配信元（本番は CloudFront）の 4 つ
   */
  private firstPartyHosts(): string[] {
    const hosts = [
      window.location.host,
      hostOf(import.meta.env.VITE_API_DOMAIN),
      hostOf(import.meta.env.VITE_ROMANEE_API_DOMAIN),
      hostOf(import.meta.url),
    ]
    return hosts.filter((host): host is string => host !== null)
  }

  /**
   * カスタムエラーをDatadogに送信する
   * @param error - エラーオブジェクト
   * @param context - 追加のコンテキスト情報（エラーの発生箇所、ユーザー操作など）
   * @param level - ログレベル。既定は error。利用者起因（入力不備・未ログイン 403・通信断）は呼び出し側で warn / info に下げる
   */
  trackError(error: Error, context?: Record<string, any>, level: LogLevel = 'error'): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    // エラーログとして送信（エラー名、スタックトレース、追加コンテキストを含む）
    datadogLogs.logger[level](error.message, {
      error: {
        kind: error.name,
        stack: error.stack,
      },
      ...context,
    })
  }

  /**
   * カスタムイベントをDatadogに送信する
   * ユーザーの行動追跡やアプリケーションの重要なイベントの記録に使用
   * @param name - イベント名
   * @param attributes - イベントの属性情報
   */
  trackEvent(name: string, attributes?: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    // イベントをInfoレベルのログとして送信
    datadogLogs.logger.info(`Event: ${name}`, {
      event_name: name,
      ...attributes,
    })
  }

  /**
   * ユーザーコンテキストを設定する
   * ログインしたユーザーのIDをグローバルコンテキストに設定し、
   * 以降のすべてのログにユーザーIDが自動的に付与される
   * @param user - ユーザー情報（idのみが使用される）
   */
  setUser(user: { id?: string; name?: string; email?: string; [key: string]: any }): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    // ログインしている場合のみuser_idを設定（プライバシー保護のため最小限の情報のみ）
    if (user.id) {
      datadogLogs.setGlobalContextProperty('user_id', user.id)
    }
  }

  /**
   * ユーザーコンテキストをクリアする
   * ログアウト時に呼び出され、user_idをグローバルコンテキストから削除する
   */
  clearUser(): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    // user_idのみを削除（他のグローバルコンテキストは維持）
    datadogLogs.removeGlobalContextProperty('user_id')
  }

  /**
   * グローバルコンテキストにカスタムプロパティを追加する
   * 追加されたプロパティは以降のすべてのログに自動的に含まれる
   * @param key - プロパティ名
   * @param value - プロパティ値
   */
  addGlobalContext(key: string, value: any): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    datadogLogs.setGlobalContextProperty(key, value)
  }

  /**
   * グローバルコンテキストから特定のプロパティを削除する
   * @param key - 削除するプロパティ名
   */
  removeGlobalContext(key: string): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    datadogLogs.removeGlobalContextProperty(key)
  }

  /**
   * カスタムログメッセージを送信する
   * 任意のログレベルでメッセージを記録できる汎用メソッド
   * @param message - ログメッセージ
   * @param context - 追加のコンテキスト情報
   * @param level - ログレベル（debug, info, warn, error）、デフォルトはinfo
   */
  log(message: string, context?: Record<string, any>, level: 'debug' | 'info' | 'warn' | 'error' = 'info'): void {
    if (!this.initialized) {
      console.warn('Datadog Logs is not initialized')
      return
    }

    datadogLogs.logger[level](message, context)
  }
}

/**
 * DatadogServiceのシングルトンインスタンス
 * アプリケーション全体で共有される単一のインスタンス
 */
export const datadogService = new DatadogService()
